import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

const SiteContentContext = createContext(null);

// Default content structure (fallback only)
const defaultContent = {
  site_name: 'Ikizamini',
  site_tagline: 'AI-powered quiz platform',
  site_description: 'Master any subject with AI-powered practice tests',
  logo_url: '',
  show_logo: true,
  hero_title: '',
  hero_subtitle: '',
  hero_cta: 'Get Started',
  features: [],
  stats: [],
  pricing_title: '',
  pricing_subtitle: '',
  pricing_note: '',
  articles_title: '',
  articles_subtitle: '',
  contact_email: '',
  contact_phone: '',
  contact_address: '',
  social_links: {},
  modal_welcome_title: '',
  modal_welcome_subtitle: '',
  modal_terms_text: '',
  topic_modal_section_title: '',
  topic_modal_feature1_title: '',
  topic_modal_feature1_desc: '',
  topic_modal_feature2_title: '',
  topic_modal_feature2_desc: '',
  topic_modal_feature3_title: '',
  topic_modal_feature3_desc: '',
  topic_modal_demo_note: ''
};

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [currentLang, setCurrentLang] = useState(() => localStorage.getItem('preferred_language') || 'en');

  // Update document metadata when content changes
  useEffect(() => {
    if (content.site_name) {
      const tagline = content.site_tagline ? ` | ${content.site_tagline}` : '';
      document.title = `${content.site_name}${tagline}`;
    }
    if (content.site_description) {
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', content.site_description);
      }
    }
  }, [content.site_name, content.site_tagline, content.site_description]);

  // Fetch content from API with language support
  const fetchContent = useCallback(async (lang) => {
    const langToUse = lang || currentLang;
    try {
      const response = await axios.get(`${API}/landing-content?lang=${langToUse}&t=${Date.now()}`);
      if (response.data) {
        setContent(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch site content');
    } finally {
      setLoading(false);
    }
  }, [currentLang]);

  useEffect(() => {
    fetchContent(currentLang);
    
    // Listen for content updates from admin panel
    const handleContentUpdate = () => {
      fetchContent(currentLang);
    };
    
    // Listen for language changes
    const handleLanguageChange = () => {
      const newLang = localStorage.getItem('preferred_language') || 'en';
      if (newLang !== currentLang) {
        setCurrentLang(newLang);
        fetchContent(newLang);
      }
    };
    
    window.addEventListener('site-content-updated', handleContentUpdate);
    window.addEventListener('storage', handleLanguageChange);
    
    // Also refresh when window gains focus (in case admin edited in another tab)
    const handleFocus = () => {
      const newLang = localStorage.getItem('preferred_language') || 'en';
      fetchContent(newLang);
    };
    window.addEventListener('focus', handleFocus);
    
    // Check for language changes periodically (for same-tab updates)
    const langCheckInterval = setInterval(() => {
      const newLang = localStorage.getItem('preferred_language') || 'en';
      if (newLang !== currentLang) {
        setCurrentLang(newLang);
        fetchContent(newLang);
      }
    }, 500);
    
    return () => {
      window.removeEventListener('site-content-updated', handleContentUpdate);
      window.removeEventListener('storage', handleLanguageChange);
      window.removeEventListener('focus', handleFocus);
      clearInterval(langCheckInterval);
    };
  }, [fetchContent, currentLang]);

  // Refresh content (can be called manually)
  const refreshContent = useCallback((lang) => {
    setLoading(true);
    fetchContent(lang || currentLang);
  }, [fetchContent, currentLang]);

  return (
    <SiteContentContext.Provider value={{ content, loading, refreshContent, currentLang }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within SiteContentProvider');
  }
  return context;
}

// Helper to notify content was updated (call from admin after saving)
export function notifySiteContentUpdated() {
  // Clear any cached landing content
  try {
    const { api } = require('../utils/apiCache');
    api.clearLandingCache();
  } catch (e) {
    // Cache module might not be available
  }
  window.dispatchEvent(new Event('site-content-updated'));
}

export default SiteContentContext;
